import { useCallback, useState } from "react";
import { SquarePlus, SquareMinus } from "lucide-react";
import { menuItems } from "./HeaderData";
import { Link, useNavigate } from "react-router-dom";
import { IoMdArrowDropup } from "react-icons/io";
import { MdOutlineArrowDropDown } from "react-icons/md";
import { getLegues, getMatchesList } from "../../api";
import { setMarketName, setRunningData } from "../../redux/slices/fullmarketSlice";
import { useDispatch, useSelector } from "react-redux";
import { SelectUserLockApplications } from "../../utils/helper/commonSelectors";

// Sub-component for each menu item
const MenuItem = ({ item, depth = 0, isOpen, toggleMenu, menuData, isLoggedIn }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openSubMenu, setOpenSubMenu] = useState(null);
  const [matchList, setMatchList] = useState([]);

  const hasSubItems = item.subItems?.length > 0;
  const eventData = menuData[item.event] || [];

  const toggleSubMenu = async (marketId, eventId) => {
    setOpenSubMenu(prev => (prev === marketId ? null : marketId));
    if (marketId && eventId) {
      try {
        const res = await getMatchesList(eventId, marketId);
        setMatchList(res.data.data || []);
      } catch (error) {
        console.error("Error fetching matches:", error);
      }
    }
  };

  const handleMarket = (runningData, markets, match) => {
    dispatch(setRunningData(runningData));
    dispatch(setMarketName(markets));
    navigate(
      `/full-market?eventType=${match.sportId}&eventId=${match.eventId}&marketId=${match.marketId}&joinedMarketIds=${match.joinedMarketIds}&competitionId=${match.competitionId}&bm=${match.bm}&fancy=${match.fancy}&pc=${e?.pc}&eventName=${e?.eventName}`
    );
  };

  const getBackgroundColorClass = (depth) => {
    const bgClasses = [
      "bg-bgfooter",
      "bg-white text-black",
      "bg-white text-black",
      "bg-white text-offwhite",
    ];
    return bgClasses[depth] || "bg-gray2";
  };

  return (
    <div key={item.label}>
      <Link to={item.link === "/user" ? (isLoggedIn ? "/user" : "/demo") : item.link}>
        <div
          className={`flex items-center justify-between py-2 pl-3 pr-1 cursor-pointer hover:bg-[#27a6c3] hover:text-white w-full hide-scrollbar ${getBackgroundColorClass(depth)}`}
          onClick={(e) => {
            if (hasSubItems) {
              e.preventDefault();
              toggleMenu(item.label, item.event);
            }
          }}
        >
          <div className="flex items-center" style={{ paddingLeft: `${depth * 16}px` }}>
            {depth === 0 && item.icon && (
              typeof item.icon === "string" ? (
                <img src={item.icon} className="size-5 mr-2" alt="icon" />
              ) : (
                <item.icon className="size-5 mr-2" />
              )
            )}
            <span className={depth > 0 ? "text-[12px]" : "text-[14px] font-[700] font-sans"}>
              {item.label}
            </span>
          </div>
          {hasSubItems && (
            isOpen ? (
              depth === 0 ? <IoMdArrowDropup size={20} /> : <SquareMinus size={11} />
            ) : (
              depth === 0 ? <MdOutlineArrowDropDown size={24} /> : <SquarePlus size={11} />
            )
          )}
        </div>
      </Link>

      {isOpen && hasSubItems && (
        <div>
          {eventData?.length > 0 ? (
            eventData.map((e, i) => (
              <div key={i} className="border-b border-gray-300">
                <div
                  className="py-2 px-4 flex justify-between items-center hover:bg-gray-200 cursor-pointer transition duration-200"
                  onClick={() => toggleSubMenu(e.competitionId, item.event)}
                >
                  <p className="text-[10px] text-gray-700 font-bold">{e.competitionName}</p>
                  <span className="text-[12px] font-bold">
                    {openSubMenu === e.competitionId ? "−" : "+"}
                  </span>
                </div>
                {openSubMenu === e.competitionId && (
                  <div>
                    {matchList?.map((match, idx) => (
                      <div
                        key={idx}
                        onClick={() => handleMarket(match.matchRunners, match.markets, match)}
                        className="py-2 mt-[-8px] px-4 pl-6 border-t border-gray bg-gray-100 cursor-pointer"
                      >
                        <p className="text-[11px]">{match.eventName}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="py-2 px-4 border-b border-gray-300 hover:bg-gray-200 cursor-pointer">
              <p className="text-sm text-gray-500">Loading...</p>
            </div>
          )}
        </div>
      )}
      <hr className="text-[#d4d0d0]" />
    </div>
  );
};

const LoggedInSidebar = () => {
  const [openMenus, setOpenMenus] = useState([]);
  const [menuData, setMenuData] = useState({});
  const isLoggedIn = useSelector(state => state.user.isLoggedIn);
  const lockData = useSelector(SelectUserLockApplications());

  const sportLockStatus = {
    cricket: lockData?.cricket,
    soccer: lockData?.soccer,
    tennis: lockData?.tennis,
    kabaddi: lockData?.kabaddi,
    horse: lockData?.horse,
    greyhound: lockData?.greyhound,
  };

  const filteredMenuItems = menuItems.filter(item => !sportLockStatus[item?.label?.toLowerCase()]);

  const toggleMenu = useCallback((menuLabel, eventId) => {
    setOpenMenus(prev =>
      prev.includes(menuLabel) ? prev.filter(item => item !== menuLabel) : [...prev, menuLabel]
    );
    if (!menuData[eventId]) {
      getLegues(eventId)
        .then(res => setMenuData(prev => ({ ...prev, [eventId]: res.data.data })))
        .catch(err => console.error("Error fetching leagues:", err));
    }
  }, [menuData]);

  return (
    <nav className="w-full bg-white overflow-y-scroll text-black hide-scrollbar md:w-auto sticky top-[149px] lg:block hidden lg:h-[85vh] border-r-4">
      <div className="p-1.5 bg-[#27A6C3] text-white text-[13px] font-[700] font-sans">
        Popular
      </div>
      <div className="pt-2.5">
        {filteredMenuItems.map(item => (
          <MenuItem
            key={item.label}
            item={item}
            isOpen={openMenus.includes(item.label)}
            toggleMenu={toggleMenu}
            menuData={menuData}
            isLoggedIn={isLoggedIn}
          />
        ))}
      </div>
    </nav>
  );
};

export default LoggedInSidebar;
